import common from "../common";
import GelButton from "../ui/gel/GelButton";
import GelScene from "../ui/gel/GelScene";
import {isIE11, isIE11Win10} from "../utils";

export default class MapPreloaderScene extends GelScene {
    constructor() {
        super();

        this.loaded = 0;

        this._bindings = {};

        this._showButton = false;

        this._bg = null;
        this._mapHolder = null;
        this._mapScaleHolder = null;
        this._mapCenter = new PIXI.Point();
        this._map = null;
        this._isLoaded = false;

        this._locationVO = null;
        this._locationVOFinished = false;

        this._bindings.progress = P3.assets.signals.progress.add((event) => this.onLoadingProgress(event));
        this._bindings.complete = P3.assets.signals.complete.once((event) => this.onLoadingComplete(event));
    }

    init() {
        super.init();

        this._showButton = common.location.map_button;

        this.initMap();
        this.initTipPanel();
    }

    destroy() {
        this._bindings.progress.detach();
        this._bindings.complete.detach();

        if (this._locationVO)
        {
            this._locationVO.destroy();
            this._locationVO = null;
        }

        // clean up goes here
        super.destroy();
    }

    resize() {
        super.resize();

        this._bg.width = P3.View.width * 3;
        this._bg.height = P3.View.height * 3;
    }

    update() {
        super.update();

        if (Math.round(this.loaded * 100) === 100 && !this._isLoaded) {
            this._isLoaded = true;
            this.animateIn();
        }
    }

    appear() {
        if (typeof common.location.audio.vo !== "string") {
            this._locationVO = P3.audio.playVO(common.location.audio.vo[common.difficulty.level], 1, true);
        } else {
            this._locationVO = P3.audio.playVO(common.location.audio.vo, 1, true);
        }

        if (this._locationVO && this._showButton) {
            if (isIE11() || isIE11Win10()) {
                this._locationVOFinished = true;
                this.setAllButtonsEnabled(false);

                if (this._isLoaded) {
                    this.signals.next.dispatch(this);
                }
            } else {
                this._locationVO.signals.end.once(() => {
                    this._locationVOFinished = true;
                    this.setAllButtonsEnabled(false);

                    if (this._isLoaded) {
                        this.signals.next.dispatch(this);
                    }
                });
            }
        }
    }

    animateIn() {
        if (this.parent === null) return;

        const settings = common.gmi.getAllSettings();
        if (!this._showButton) {
            if (settings.motion) {
                const target = new PIXI.Point(common.location.map.x - this._map.texture.width * 0.5,
                    common.location.map.y - this._map.texture.width * 0.5);

                new P3.utils.TWEEN.Tween(this._mapScaleHolder.scale, this._tweens)
                    .to({x: 0.74, y: 0.74}, 988)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .repeat(1)
                    .yoyo(true)
                    .start();

                new P3.utils.TWEEN.Tween(this._mapHolder, this._tweens)
                    .to({x: this._mapCenter.x - target.x, y: this._mapCenter.y - target.y}, 2000)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .onComplete(() => {
                        P3.utils.delayg(this._tweens, 1, () => this.signals.next.dispatch(this));
                    })
                    .start();
            } else {
                P3.utils.delayg(this._tweens, 1, () => this.signals.next.dispatch(this));
            }
        } else {
            if (settings.motion) {
                const target = new PIXI.Point(common.location.map.x - this._map.texture.width * 0.5,
                    common.location.map.y - this._map.texture.width * 0.5);

                new P3.utils.TWEEN.Tween(this._mapScaleHolder.scale, this._tweens)
                    .to({x: 1, y: 1}, 4000)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .start();

                new P3.utils.TWEEN.Tween(this._mapHolder, this._tweens)
                    .to({x: this._mapCenter.x - target.x, y: this._mapCenter.y - target.y}, 4000)
                    .easing(P3.utils.TWEEN.Easing.Quadratic.InOut)
                    .start();
            }
        }

        const to = this._tip.y;
        this._tip.y += 100;
        new P3.utils.TWEEN.Tween(this._tip, this._tweens)
            .to({y: to}, 400)
            .easing(P3.utils.TWEEN.Easing.Quadratic.Out)
            .onStart(() => {
                this._tip.visible = true;
            })
            .start();
    }

    initTipPanel() {
        this._tip = new PIXI.Container();
        this._tip.position.set(common.STAGE_WIDTH * 0.5, P3.View.height - 28);
        this._tip.visible = false;
        this.addChild(this._tip);

        this._tip.image = new PIXI.mesh.NineSlicePlane(P3.assets.texture("answer_box_9slice"), 88, 44, 88, 44);
        this._tip.image.width = 800;
        this._tip.image.pivot.set(this._tip.image.width * 0.5, this._tip.image.height * 0.5);
        this._tip.addChild(this._tip.image);

        this._tip.label = new PIXI.extras.BitmapText("Have your brain, pencil and paper ready!", {font: "34px BBC Reith Sans"});
        this._tip.label.position.set(0, -10);
        this._tip.label.anchor.set(0.5);
        this._tip.label.tint = 0x2B365A;
        this._tip.addChild(this._tip.label);
    }

    initMap() {
        this._mapCenter.set(common.STAGE_WIDTH * 0.5, P3.View.height * 0.5);

        this._mapHolder = new PIXI.Container();
        this._mapHolder.position.set(this._mapCenter.x, this._mapCenter.y);
        this._mapHolder.interactive = true;
        this._mapHolder.hitArea = new PIXI.Rectangle(-Number.MAX_VALUE * 0.5, -Number.MAX_VALUE * 0.5, Number.MAX_VALUE, Number.MAX_VALUE);
        this.addChild(this._mapHolder);

        const settings = common.gmi.getAllSettings();

        this._mapScaleHolder = new PIXI.Container();
        this._mapScaleHolder.scale.set(this._showButton && settings.motion ? 0.6 : 1);
        this._mapHolder.addChild(this._mapScaleHolder);

        this._bg = new PIXI.extras.TilingSprite(P3.assets.texture("wooden_table_texture"));
        this._bg.anchor.set(0.5);
        this._mapScaleHolder.addChild(this._bg);

        this._map = new PIXI.Sprite(P3.assets.texture("map_2048"));
        this._map.anchor.set(0.5);
        this._mapScaleHolder.addChild(this._map);
    }

    onLoadingProgress(event) {
        this.loaded = event.progress / 100;
        console.log("loading:", event.progress);
    }

    onLoadingComplete(event) {
        this.loaded = 1;

        if (this._showButton) {
            this.initSkipButton(0, GelButton.POSITION_MIDDLE_LOW, GelScene.SIZE_LARGE);
            if (this._locationVOFinished) {
                P3.utils.delayg(this._tweens, 2, () => {
                    this.signals.next.dispatch(this);
                });
            }
        }
    }
}